import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PartnerRestaurants } from "@/components/restaurant/PartnerRestaurants";
import {
  ShoppingBag, Star, ChevronLeft, Search, Filter, Heart,
  Plus, Minus, ShieldCheck, UtensilsCrossed, Flame, Trash2, X, Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  UtensilsCrossed: UtensilsCrossed,
  Flame: Flame,
  Star: Star,
  ShoppingBag: ShoppingBag,
};

const formatFCFA = (n) => Number(n || 0).toLocaleString("fr-FR");

export default function FoodPage({ user }) {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [dishes, setDishes] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("food_favs") || "[]");
    } catch {
      return [];
    }
  });
  const [cart, setCart] = useState({ items: [], subtotal: 0, delivery_fee: 0, total: 0, item_count: 0 });
  const [showCart, setShowCart] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [checkoutAddress, setCheckoutAddress] = useState("");
  const [checkoutPayment, setCheckoutPayment] = useState("cash");
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // --- Load categories + dishes ---
  useEffect(() => {
    axios
      .get(`${API}/food/categories`)
      .then((r) => setCategories(r.data.categories || []))
      .catch(() => setCategories([]));
  }, []);

  const fetchDishes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeCategory) params.set("category", activeCategory);
      if (search.trim()) params.set("search", search.trim());
      const { data } = await axios.get(`${API}/food/dishes?${params.toString()}`);
      setDishes(data.dishes || []);
    } catch (err) {
      console.warn("fetchDishes failed:", err);
      setDishes([]);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, search]);

  useEffect(() => {
    const t = setTimeout(fetchDishes, 220); // debounce search
    return () => clearTimeout(t);
  }, [fetchDishes]);

  // --- Cart sync ---
  const fetchCart = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await axios.get(`${API}/food/cart/${user.id}`);
      setCart(data);
    } catch (err) {
      console.warn("cart fetch failed:", err);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const toggleFavorite = (dishId) => {
    const next = favorites.includes(dishId)
      ? favorites.filter((id) => id !== dishId)
      : [...favorites, dishId];
    setFavorites(next);
    localStorage.setItem("food_favs", JSON.stringify(next));
  };

  const addToCart = async (dish) => {
    if (!user?.id) {
      toast.error("Connectez-vous pour commander");
      navigate("/auth");
      return;
    }
    setAddingId(dish.id);
    try {
      const { data } = await axios.post(`${API}/food/cart/add`, {
        user_id: user.id,
        dish_id: dish.id,
        quantity: 1,
      });
      setCart(data);
      toast.success(`${dish.name} ajouté au panier`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'ajouter au panier");
    } finally {
      setAddingId(null);
    }
  };

  const updateQty = async (cart_item_id, quantity) => {
    if (!user?.id) return;
    try {
      const { data } = await axios.post(`${API}/food/cart/update`, {
        user_id: user.id,
        cart_item_id,
        quantity,
      });
      setCart(data);
    } catch (err) {
      toast.error("Mise à jour impossible");
    }
  };

  const clearCart = async () => {
    if (!user?.id) return;
    try {
      const { data } = await axios.delete(`${API}/food/cart/${user.id}`);
      setCart(data);
      toast.info("Panier vidé");
    } catch (err) {
      console.warn("clearCart failed:", err);
    }
  };

  const checkout = async () => {
    if (!user?.id) {
      toast.error("Connectez-vous");
      return;
    }
    if (!checkoutAddress.trim()) {
      toast.error("Adresse de livraison requise");
      return;
    }
    setCheckoutLoading(true);
    try {
      const { data } = await axios.post(`${API}/food/checkout`, {
        user_id: user.id,
        delivery_address: checkoutAddress.trim(),
        payment_method: checkoutPayment,
      });
      toast.success(data.message || "Commande créée !");
      setShowCart(false);
      setCart({ items: [], subtotal: 0, delivery_fee: 0, total: 0, item_count: 0 });
      navigate(`/tracking?id=${data.order_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la commande");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const promoBannerDishes = useMemo(() => dishes.slice(0, 2), [dishes]);

  return (
    <div className="min-h-screen bg-[#0F172A] text-white font-sans pb-32 overflow-x-hidden" data-testid="food-page">
      {/* ===================== HEADER ===================== */}
      <header className="p-6 pt-8 space-y-6 relative">
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#D4AF37]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -top-20 -left-20 w-72 h-72 bg-[#003594]/20 rounded-full blur-3xl pointer-events-none" />

        <div className="relative flex justify-between items-center">
          <button
            onClick={() => navigate("/home")}
            className="bg-[#1E293B] p-3 rounded-2xl border border-white/5 text-[#D4AF37] hover:border-[#D4AF37]/40 transition-all"
            data-testid="food-back-btn"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="text-center">
            <h1 className="text-xs font-black text-[#D4AF37] uppercase tracking-[0.3em]">TAF-TAF Gourmet</h1>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Dakar · Plateau</p>
          </div>
          <button
            onClick={() => setShowCart(true)}
            className="bg-[#1E293B] p-3 rounded-2xl border border-white/5 relative hover:border-[#D4AF37]/40 transition-all"
            data-testid="food-cart-btn"
          >
            <ShoppingBag size={20} />
            {cart.item_count > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] text-[10px] font-black text-[#0F172A] flex items-center justify-center rounded-full border border-[#0F172A]" data-testid="food-cart-count">
                {cart.item_count}
              </span>
            )}
          </button>
        </div>

        <div className="relative group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-[#D4AF37] transition-colors" size={18} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un plat ou un restaurant..."
            className="w-full bg-[#1E293B] border border-white/5 rounded-[1.5rem] py-4 pl-12 pr-4 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#D4AF37]/50 transition-all"
            data-testid="food-search-input"
          />
        </div>
      </header>

      {/* ===================== CATEGORIES ===================== */}
      <section className="px-6 overflow-x-auto flex gap-3 no-scrollbar pb-2" data-testid="food-categories">
        {categories.map((cat) => {
          const Icon = CATEGORY_ICONS[cat.icon] || UtensilsCrossed;
          const isActive = activeCategory === cat.slug;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.slug)}
              className={`flex items-center gap-2 px-5 py-3 rounded-2xl text-xs font-black uppercase tracking-widest whitespace-nowrap transition-all ${
                isActive
                  ? "bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] shadow-lg shadow-[#D4AF37]/20"
                  : "bg-[#1E293B] text-slate-400 border border-white/5 hover:border-[#D4AF37]/30 hover:text-white"
              }`}
              data-testid={`food-cat-${cat.slug}`}
            >
              <Icon size={16} />
              {cat.name}
            </button>
          );
        })}
      </section>

      {/* ===================== PROMO BANNER ===================== */}
      <section className="px-6 mt-8">
        <div className="relative bg-gradient-to-r from-[#003594] via-[#003380] to-[#0F172A] rounded-[2.5rem] p-8 overflow-hidden group border border-white/5" data-testid="food-promo-banner">
          <div className="relative z-10">
            <span className="bg-white/10 backdrop-blur-md px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest text-[#D4AF37]">
              Offre de Bienvenue
            </span>
            <h2 className="text-3xl font-black mt-3 leading-none" style={{fontFamily: 'Outfit, sans-serif'}}>
              LIVRAISON<br />OFFERTE <span className="text-[#D4AF37]">*</span>
            </h2>
            <p className="text-[10px] text-blue-200 mt-2 font-bold uppercase tracking-widest">
              Sur vos 3 premières commandes Gourmet
            </p>
          </div>
          <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-[#D4AF37] rounded-full blur-[70px] opacity-30" />
          <div className="absolute right-6 top-1/2 -translate-y-1/2 opacity-20 group-hover:scale-110 transition-transform">
            <UtensilsCrossed size={80} strokeWidth={1} />
          </div>
        </div>
      </section>

      {/* ===================== DISHES LIST ===================== */}
      <PartnerRestaurants onAdd={addToCart} adding={addingId} />
      <section className="px-6 mt-10 space-y-6">
        <div className="flex justify-between items-end">
          <h3 className="text-xl font-black uppercase tracking-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
            Nos <span className="text-[#D4AF37]">Incontournables</span>
          </h3>
          <Filter size={18} className="text-[#D4AF37]" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : dishes.length === 0 ? (
          <div className="bg-[#1E293B] border border-white/10 rounded-[2rem] py-12 px-6 text-center">
            <UtensilsCrossed className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Aucun plat trouvé.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8" data-testid="food-dishes-grid">
            {dishes.map((dish) => {
              const isFav = favorites.includes(dish.id);
              const isAdding = addingId === dish.id;
              return (
                <div
                  key={dish.id}
                  className="group relative bg-[#1E293B]/60 rounded-[3rem] border border-white/5 overflow-hidden transition-all hover:border-[#D4AF37]/40"
                  data-testid={`food-dish-${dish.id}`}
                >
                  {/* Image */}
                  <div className="relative h-60 w-full overflow-hidden">
                    <img
                      src={dish.image_url}
                      alt={dish.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1565958011703-44f9829ba187?auto=format&fit=crop&w=800&q=80"; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/30 to-transparent" />
                    <button
                      onClick={() => toggleFavorite(dish.id)}
                      className={`absolute top-6 right-6 p-3 backdrop-blur-md rounded-2xl transition-colors ${
                        isFav ? "bg-red-500/20 text-red-400" : "bg-black/30 text-white hover:text-red-400"
                      }`}
                      data-testid={`food-fav-${dish.id}`}
                    >
                      <Heart size={18} fill={isFav ? "currentColor" : "none"} />
                    </button>
                    {dish.tag && (
                      <div className="absolute top-6 left-6 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest shadow-lg shadow-[#D4AF37]/20">
                        {dish.tag}
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div className="p-6 -mt-12 relative z-10 bg-gradient-to-b from-transparent to-[#1E293B]/95 backdrop-blur-sm rounded-t-[3rem]">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-xl font-black text-white" style={{fontFamily: 'Outfit, sans-serif'}}>{dish.name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <ShieldCheck size={13} className="text-[#D4AF37] flex-shrink-0" />
                          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest truncate">{dish.restaurant_name}</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xl font-black text-[#D4AF37]" data-testid={`food-price-${dish.id}`}>
                          {formatFCFA(dish.price)} <span className="text-[10px]">F</span>
                        </p>
                        <div className="flex items-center justify-end gap-1 mt-1">
                          <Star size={11} fill="#D4AF37" className="text-[#D4AF37]" />
                          <span className="text-xs font-bold">{dish.rating}</span>
                        </div>
                      </div>
                    </div>

                    {dish.description && (
                      <p className="text-xs text-slate-400 mt-3 leading-relaxed line-clamp-2">{dish.description}</p>
                    )}

                    <div className="flex items-center justify-between mt-5">
                      <div className="flex items-center gap-2 text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                        <Flame size={12} className="text-[#D4AF37]" />
                        {dish.prep_time_min}-{dish.prep_time_max} min
                      </div>
                      <button
                        onClick={() => addToCart(dish)}
                        disabled={isAdding}
                        className="group/btn flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 text-[#0F172A] rounded-full px-5 py-2.5 text-xs font-black uppercase tracking-widest shadow-lg shadow-[#D4AF37]/20 transition-all active:scale-95 disabled:opacity-60"
                        data-testid={`food-add-${dish.id}`}
                      >
                        {isAdding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus size={14} />}
                        Ajouter
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ===================== STICKY CART BAR ===================== */}
      {cart.item_count > 0 && !showCart && (
        <div className="fixed bottom-5 left-5 right-5 z-40 animate-in slide-in-from-bottom-4 duration-300" data-testid="food-sticky-cart">
          <button
            onClick={() => setShowCart(true)}
            className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] rounded-full py-4 px-6 shadow-2xl shadow-[#D4AF37]/30 flex items-center justify-between hover:opacity-95"
          >
            <span className="flex items-center gap-3 font-black uppercase tracking-widest text-xs">
              <span className="bg-[#0F172A] text-[#D4AF37] rounded-full w-7 h-7 flex items-center justify-center">
                {cart.item_count}
              </span>
              Voir mon panier
            </span>
            <span className="font-black text-sm">{formatFCFA(cart.total)} F</span>
          </button>
        </div>
      )}

      {/* ===================== CART DRAWER ===================== */}
      {showCart && (
        <div className="fixed inset-0 z-[9998] bg-[#0F172A]/90 backdrop-blur-md flex items-end md:items-center justify-center px-0 md:px-4" data-testid="food-cart-drawer">
          <div className="w-full md:max-w-lg max-h-[92vh] bg-[#1E293B] border-t md:border border-[#D4AF37]/30 rounded-t-[2.5rem] md:rounded-[2.5rem] flex flex-col overflow-hidden shadow-2xl shadow-[#D4AF37]/20 animate-in slide-in-from-bottom-10 duration-300">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
              <div>
                <h3 className="text-lg font-black uppercase tracking-tight text-white" style={{fontFamily: 'Outfit, sans-serif'}}>
                  Mon <span className="text-[#D4AF37]">panier</span>
                </h3>
                <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
                  {cart.item_count} article{cart.item_count > 1 ? "s" : ""}
                </p>
              </div>
              <button
                onClick={() => setShowCart(false)}
                className="p-2 rounded-full bg-[#0F172A] text-slate-400 hover:text-white border border-white/5"
                data-testid="food-cart-close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {cart.items.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">Votre panier est vide</p>
                </div>
              ) : (
                cart.items.map((it) => (
                  <div key={it.cart_item_id} className="flex items-center gap-3 bg-[#0F172A] border border-white/5 rounded-2xl p-3" data-testid={`cart-item-${it.cart_item_id}`}>
                    <img src={it.image_url} alt={it.name} className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-black text-white truncate">{it.name}</p>
                      <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold truncate">{it.restaurant_name}</p>
                      <p className="text-sm font-black text-[#D4AF37] mt-1">{formatFCFA(it.price * it.quantity)} F</p>
                    </div>
                    <div className="flex items-center gap-1 bg-[#1E293B] border border-white/10 rounded-full p-1">
                      <button
                        onClick={() => updateQty(it.cart_item_id, it.quantity - 1)}
                        className="w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:text-white"
                        data-testid={`cart-minus-${it.cart_item_id}`}
                      >
                        {it.quantity === 1 ? <Trash2 size={13} /> : <Minus size={14} />}
                      </button>
                      <span className="text-sm font-black w-6 text-center">{it.quantity}</span>
                      <button
                        onClick={() => updateQty(it.cart_item_id, it.quantity + 1)}
                        className="w-7 h-7 rounded-full flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/10"
                        data-testid={`cart-plus-${it.cart_item_id}`}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}

              {cart.items.length > 0 && (
                <>
                  {/* Checkout form */}
                  <div className="pt-4 space-y-3">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Adresse de livraison *</label>
                      <input
                        type="text"
                        value={checkoutAddress}
                        onChange={(e) => setCheckoutAddress(e.target.value)}
                        placeholder="Ex: Médina, Rue 15 x Boulevard Martin"
                        className="w-full bg-[#0F172A] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4AF37]/50"
                        data-testid="food-checkout-address"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Mode de paiement</label>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { id: "cash", label: "Cash" },
                          { id: "wave", label: "Wave" },
                          { id: "orange_money", label: "Orange" },
                          { id: "free_money", label: "Free" },
                        ].map((pm) => (
                          <button
                            key={pm.id}
                            type="button"
                            onClick={() => setCheckoutPayment(pm.id)}
                            className={`py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                              checkoutPayment === pm.id
                                ? "bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] shadow-md"
                                : "bg-[#0F172A] text-slate-400 border border-white/10 hover:border-[#D4AF37]/30"
                            }`}
                            data-testid={`food-pay-${pm.id}`}
                          >
                            {pm.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer totals + CTA */}
            {cart.items.length > 0 && (
              <div className="border-t border-white/5 px-6 py-5 bg-[#0F172A] space-y-3">
                <div className="flex justify-between text-xs text-slate-400">
                  <span>Sous-total</span>
                  <span>{formatFCFA(cart.subtotal)} F</span>
                </div>
                <div className="flex justify-between text-xs text-slate-400">
                  <span>Livraison</span>
                  <span>{formatFCFA(cart.delivery_fee)} F</span>
                </div>
                <div className="flex justify-between items-center font-black">
                  <span className="text-sm uppercase tracking-widest text-white">Total</span>
                  <span className="text-2xl text-[#D4AF37]" data-testid="food-cart-total">{formatFCFA(cart.total)} F</span>
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={clearCart}
                    className="px-4 py-3 bg-[#1E293B] border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-400/40 rounded-full text-xs font-black uppercase tracking-widest"
                    data-testid="food-cart-clear"
                  >
                    <Trash2 size={14} />
                  </button>
                  <button
                    onClick={checkout}
                    disabled={checkoutLoading}
                    className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 text-[#0F172A] rounded-full py-3 text-xs font-black uppercase tracking-widest shadow-xl shadow-[#D4AF37]/30 disabled:opacity-60 flex items-center justify-center gap-2"
                    data-testid="food-checkout-btn"
                  >
                    {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Commander"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
